import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Plus } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { LoadingBlock, ErrorState, Button, toast } from "@/components/ds";
import { PageTitle, Stat, Badge, Card, money, pct } from "./_ui";

export default function RepDetail() {
  const { userId } = useParams();
  const qc = useQueryClient();
  const [adj, setAdj] = useState({ amount: "", reason: "", type: "adjustment" });
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["rep-detail", userId], queryFn: () => api.salesRepDetail(userId!) });
  if (isLoading) return <LoadingBlock rows={6} />;
  if (isError || !data) return <ErrorState onRetry={refetch} />;
  const s = data.summary;

  const submitAdj = async () => {
    const amount = parseFloat(adj.amount);
    if (isNaN(amount) || !adj.reason.trim()) { toast.error("Amount and reason are required."); return; }
    try {
      await api.salesCreateAdjustment({ user_id: userId!, amount, reason: adj.reason, type: adj.type });
      toast.success("Adjustment recorded.");
      setAdj({ amount: "", reason: "", type: "adjustment" });
      qc.invalidateQueries({ queryKey: ["rep-detail", userId] });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="rep-detail">
      <Link to="/internal/sales/reps" className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"><ArrowLeft className="h-4 w-4" /> All representatives</Link>
      <PageTitle title={s.rep_name} subtitle={`Period ${String(s.period_start).slice(0,10)} → ${String(s.period_end).slice(0,10)} · ${s.days_remaining} days remaining`}
        right={<Badge status={s.quota_status} />} />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Total Paid Sales" value={`${s.total_paid_sales} / ${s.quota}`} testid="rd-sales" />
        <Stat label="Growth Mix" value={pct(s.growth_mix_percent)} testid="rd-mix" />
        <Stat label="Initial Revenue" value={money(s.initial_revenue_collected)} tone="emerald" />
        <Stat label="Total Owed" value={money(s.total_owed)} tone="indigo" testid="rd-owed" />
        <Stat label="Starter" value={s.starter} /><Stat label="Growth" value={s.growth} />
        <Stat label="Pro" value={s.pro} /><Stat label="Custom" value={s.custom} />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Card title="Compensation Breakdown">
          <dl className="space-y-2 text-sm" data-testid="rd-comp">
            <Row k="Base Commission" v={money(s.base_commission)} />
            <Row k="General Performance Bonus" v={money(s.general_performance_bonus)} />
            <Row k="Growth Accelerator" v={money(s.growth_accelerator)} />
            <Row k="Pro Accelerator" v={money(s.pro_accelerator)} />
            <Row k="Custom Accelerator" v={money(s.custom_accelerator)} />
            <Row k="Adjustments" v={money(s.adjustments)} />
            <div className="my-1 border-t border-slate-100" />
            <Row k="Total Owed" v={money(s.total_owed)} bold />
            <Row k="Total Paid" v={money(s.total_paid)} />
            <Row k="Outstanding" v={money(s.outstanding)} tone={s.outstanding > 0 ? "text-amber-600" : "text-emerald-600"} />
          </dl>
        </Card>

        <Card title="Add Adjustment (platform admin)">
          <div className="space-y-3 text-sm" data-testid="rd-adjust">
            <input type="number" value={adj.amount} onChange={(e) => setAdj({ ...adj, amount: e.target.value })} placeholder="Amount (use negative for clawback)" className="w-full rounded-md border border-slate-200 px-3 py-2" data-testid="adj-amount" />
            <select value={adj.type} onChange={(e) => setAdj({ ...adj, type: e.target.value })} className="w-full rounded-md border border-slate-200 px-3 py-2" data-testid="adj-type">
              {["adjustment", "bonus", "correction", "incentive", "clawback"].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <textarea value={adj.reason} onChange={(e) => setAdj({ ...adj, reason: e.target.value })} placeholder="Reason (required, audited)" rows={2} className="w-full rounded-md border border-slate-200 px-3 py-2" data-testid="adj-reason" />
            <Button onClick={submitAdj} className="w-full" data-testid="adj-submit"><Plus className="mr-1 h-4 w-4" /> Record adjustment</Button>
          </div>
        </Card>
      </div>

      <div className="mt-6">
        <Card title="Sales in this period">
          {data.sales.length === 0 ? <p className="py-6 text-center text-sm text-slate-400">No sales in this period.</p> : (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-slate-400"><th className="py-2">Date</th><th>Business</th><th>Plan</th><th>Collected</th><th>Commissionable</th><th>%</th><th>Commission</th><th>Status</th></tr></thead>
              <tbody>
                {data.sales.map((sale: any) => (
                  <tr key={sale.id} className="border-t border-slate-50">
                    <td className="py-2">{String(sale.sale_date).slice(0,10)}</td>
                    <td>{sale.business_name}</td><td className="capitalize">{sale.plan}</td>
                    <td>{money(sale.total_actual_collected)}</td><td>{money(sale.commissionable_value)}</td>
                    <td>{sale.commission_percent}%</td><td className="font-medium">{money(sale.commission_amount)}</td>
                    <td><Badge status={sale.commission_status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </div>
  );
}

function Row({ k, v, bold, tone }: { k: string; v: string; bold?: boolean; tone?: string }) {
  return <div className="flex justify-between"><dt className="text-slate-500">{k}</dt><dd className={`${bold ? "font-semibold" : "font-medium"} ${tone || "text-slate-900"}`}>{v}</dd></div>;
}
